import { BASIC_HOTEL_FRAGMENT, ROOM_MAPPING_FRAGMENT } from '@app/model/hotel/service/HotelGraphQLFragments';
import { gql } from 'graphql-request';
import CompanyHotelPriceRecommendationSettings
    from '@app/model/company_hotel/settings/CompanyHotelPriceRecommendationSettings';

export const COMPANY_HOTEL_MAIN_SETTINGS_FRAGMENT = gql`
    fragment MainSettingsFragment on MainSettings {
        modules
        domains
        region
        currency
        marketAreaId
        enableMarketBookingCurve
        enableMarketPace
        ignorePartnerOffers
        maxNumberOfCompetitors
    }
`

export const COMPANY_HOTEL_OPERATION_SETTINGS_FRAGMENT = gql`
    fragment OperationSettingsFragment on OperationSettings {
        type
        seasonFrom
        seasonTo
    }
`

export const COMPANY_HOTEL_CALENDAR_SETTINGS_FRAGMENT = gql`
    fragment CalendarSettingsFragment on CalendarSettings {
        competitorWeights { competitorId weight }
        defaultDisplayType
        comparisonMethod
        priceMedianWarningRange{
            from
            to
        }
        availabilityWarningRange {
            from
            to
        }
        priceRankingIdealPosition
        priceRankingPositionThreshold
        availabilityIndexPositionThreshold
        calendarDayRoomDisplayType
        numberOfDaysForPriceParity
        enableMultiMonthsCalendarUpdate
        enableRefreshWithProof
        enableCountryPromotions
        marketStateGroupEvaluationMethod
        calendarDayPopupTriggerType
        parityDifferencePercentage
        priceMedianIndicationType
        priceRecommendationIndicator
        showOtherRoomTypeRecommendation
    }
`
export const COMPANY_HOTEL_CALENDAR_TABLE_SETTINGS_FRAGMENT = gql`
    fragment CalendarTableSettingsFragment on CalendarTableSettings {
        view
        columns { key visible }
        pickupWindows
    }
`

export const COMPANY_HOTEL_CALENDAR_FILTERS_SETTINGS_FRAGMENT = gql`
    fragment CalendarFiltersSettingsFragment on CalendarFiltersSettings {
        filtersArrangement {
            companyHotelId
            filter
            isHidden
            isLockedInRow
            position
            row
        }
        showSecondFiltersRow
    }
`

export const COMPANY_HOTEL_ROOM_MAPPING_SETTINGS_FRAGMENT = gql`
    fragment RoomMappingSettingsFragment on RoomMappingSettings {
        showSystemRoomMappingTypes
    }
`

export const COMPANY_HOTEL_DEFAULT_PARSING_SETTINGS_FRAGMENT = gql`
    fragment DefaultParsingSettingsFragment on DefaultParsingSettings {
        numberOfGuests
        reviewsProviders
        nights
    }
`

export const COMPANY_HOTEL_ALERT_SETTINGS_FRAGMENT = gql`
    fragment AlertSettingsFragment on AlertSettings {
        disableAlertProfileRecipientsSelection       
    }
`

export const COMPANY_HOTEL_PARITY_SETTINGS_FRAGMENT = gql`
    fragment ParitySettingsFragment on ParitySettings {
        includeAirbnb
    }
`

export const COMPANY_HOTEL_CALENDAR_EXPORT_SETTINGS_FRAGMENT = gql`
    fragment CalendarExportSettingsFragment on CalendarExportSettings {
        format
        addRoomName
        addCompetitorsRoomName
        addMealType
        addMarketDemand
    }
`

export const COMPANY_HOTEL_SETTINGS_FRAGMENT = gql`
    ${COMPANY_HOTEL_MAIN_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_OPERATION_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_CALENDAR_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_CALENDAR_TABLE_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_CALENDAR_FILTERS_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_ROOM_MAPPING_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_DEFAULT_PARSING_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_ALERT_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_PARITY_SETTINGS_FRAGMENT}
    ${COMPANY_HOTEL_CALENDAR_EXPORT_SETTINGS_FRAGMENT}
    fragment CompanyHotelSettingsFragment on CompanyHotelSettings {
        companyHotelId
        main {
            ...MainSettingsFragment
        }
        operation {
            ...OperationSettingsFragment
        }
        calendar {
            ...CalendarSettingsFragment
        }
        calendarTable {
            ...CalendarTableSettingsFragment
        }
        calendarFilters{
            ...CalendarFiltersSettingsFragment
        }
        roomMapping {
            ...RoomMappingSettingsFragment
        }
        defaultParsing {
            ...DefaultParsingSettingsFragment
        }
        alert {
            ...AlertSettingsFragment
        }
        parity {
            ...ParitySettingsFragment
        }
        calendarExport {
            ...CalendarExportSettingsFragment
        }
    }
`

export const COMPANY_HOTEL_PRICE_RECOMMENDATION_SETTINGS_FRAGMENT = gql`
    fragment CompanyHotelPriceRecommendationSettingsFields on CompanyHotelPriceRecommendationSettings{
        canSubmitPrices
        engine
        engineVersion
        showManualPriceRecommendationTrigger
        enforcePricingArchitectureHierarchy
        hiddenPricingRoomRateTypes
    }
`

export const COMPANY_HOTEL_FRAGMENT = gql`
    ${BASIC_HOTEL_FRAGMENT}
    ${COMPANY_HOTEL_SETTINGS_FRAGMENT}
    ${ROOM_MAPPING_FRAGMENT}
    ${COMPANY_HOTEL_PRICE_RECOMMENDATION_SETTINGS_FRAGMENT}
    fragment CompanyHotelFields on CompanyHotel {
        id
        webhotelierId
        competitorTags {
            id
            name
            color
            creationDate
        }
        mainHotel {
            id
            name
            type
            viewOrder
            creationDate
            tags {
                id
                name
                color
                creationDate
            }
            competitors {
                hotel {
                    ...BasicHotelFields
                }
                roomMappings {
                    ...HotelRoomWithMappingFields
                }
                hasUnmappedRooms
            }
        }
        competitors {
            id
            name
            type
            viewOrder
            creationDate
            tags {
                id
                name
                color
                creationDate
            }
            competitors {
                hotel {
                    ...BasicHotelFields
                }
                roomMappings {
                    ...HotelRoomWithMappingFields
                }
                hasUnmappedRooms
            }
        }
        metaradarSettings {
            companyHotelId
            providers
            creationDate
            regions
            nights
            threshold
            state
        }
        settings {
            ...CompanyHotelSettingsFragment
        }

        parsingSettings {
            id
            variables {
                provider
                platform
                ratesType
            }
            state
            nights
        }
        roomMappingTypeConfigurations {
            type
            name
            isSystem
            isEnabled

        }
        pricingIntegrationActive
        priceRecommendationSettings {
            ...CompanyHotelPriceRecommendationSettingsFields
        }
    }
`
